
import { defineComponent, ref, onMounted, computed } from 'vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { Kamoku } from '@/repositories/fuzoku-meisai/data'
import { kaikeikijunRepositories } from '@/repositories/fuzoku-meisai/kaikei-kijun/kaikeikijunRepositories'
import { callApiWrapper } from '@/views/callApiWrapper'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { YukashoukenUiItem } from '@/views/fuzoku-meisai/kaikei-kijun/data'
import { UiItemsLogic } from '@/views/fuzoku-meisai/uiItemsLogic'
import YukashoukenSettingSaikenItem from '@/views/fuzoku-meisai/kaikei-kijun/YukashoukenSettingSaikenItem.vue'
import YukashoukenSettingOtherItem from '@/views/fuzoku-meisai/kaikei-kijun/YukashoukenSettingOtherItem.vue'
import KaikeiKijunSettingTitle from '@/views/fuzoku-meisai/kaikei-kijun/KaikeiKijunSettingTitle.vue'

const repository = kaikeikijunRepositories.yukashouken

const yukashoukenItemType = {
  Saiken: 'Saiken',
  Other: 'Other'
} as const

type YukashoukenItemType = typeof yukashoukenItemType[keyof typeof yukashoukenItemType];

export default defineComponent({
  components: {
    KaikeiKijunSettingTitle,
    YukashoukenSettingOtherItem,
    YukashoukenSettingSaikenItem,
    ResponseResultAlert,
    ContentLayout,
    LoadingButton
  },
  setup () {
    let kamokuSource: Kamoku[] = []
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const saikenItems = ref<YukashoukenUiItem[]>([])
    const otherItems = ref<YukashoukenUiItem[]>([])
    const isAllValid = computed(() => {
      const allCount = saikenItems.value.length + otherItems.value.length
      if (allCount === 0) {
        return true
      }
      const allValidCount = saikenItems.value.filter(i => i.isValid).length + otherItems.value.filter(i => i.isValid).length
      return allCount === allValidCount
    })

    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        await refreshUiData()
      })
      loading.value = false
    })

    const onClickRegister = async () => {
      loading.value = true
      onDismissAlert()

      await callApiWrapper(requestError, async () => {
        const saikenParams = saikenItems.value.map(v => v.item)
        const otherParams = otherItems.value.map(v => v.item)
        await repository.save(saikenParams, otherParams)
        await refreshUiData()
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    const addItem = (type: YukashoukenItemType) => {
      const targets = getUiItems(type)
      const newId = UiItemsLogic.makeId(targets)
      targets.push({
        isValid: false,
        item: {
          id: newId,
          itemName: '',
          amount: 0,
          setting: {
            sources: [...kamokuSource],
            targets: []
          }
        }
      })
    }

    const upItem = (type: YukashoukenItemType, item: YukashoukenUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.upUiItem(items, item)
    }

    const downItem = (type: YukashoukenItemType, item: YukashoukenUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.downUiItem(items, item)
    }

    const trashItem = (type: YukashoukenItemType, item: YukashoukenUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.removeUiItem(items, item)
    }

    const onChangeInput = (type: YukashoukenItemType, item: YukashoukenUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.updateUiItem(items, item)
    }

    const getUiItems = (type: YukashoukenItemType) => {
      switch (type) {
        case 'Saiken':
          return saikenItems.value
        case 'Other':
          return otherItems.value
        default:
          throw Error('unknown type')
      }
    }

    const refreshUiData = async () => {
      const data = await repository.list()
      kamokuSource = data.kamokuSources
      saikenItems.value = data.saiken.map(value => ({ item: value, isValid: true }))
      otherItems.value = data.other.map(value => ({ item: value, isValid: true }))
    }

    return {
      loading,
      isAllValid,
      isSucceedRequest,
      requestError,
      saikenItems,
      otherItems,
      onClickRegister,
      onDismissAlert,
      addItem,
      upItem,
      downItem,
      trashItem,
      onChangeInput
    }
  }
})
